import { useState, useCallback, useEffect } from "react";
import { getGridFilters, setGridFilters, type GridType } from "../utils/gridFilterStorage";
import type {
  GridTraceFilters,
  GridSpanFilters,
  GridLogFilters,
  GridDurationFilter,
  GridTextFilter,
  GridSpanCountFilter,
} from "../types/filters";

/** Default trace grid filters. */
export const DEFAULT_TRACE_FILTERS: GridTraceFilters = {
  traceIds: [],
  serviceNames: [],
  serviceInstances: [],
  duration: { min: null, max: null },
  spanCount: { min: null, max: null },
  sort: "start_time",
  order: "desc",
  visibleColumns: undefined,
};

/** Default span grid filters. */
export const DEFAULT_SPAN_FILTERS: GridSpanFilters = {
  traceIds: [],
  spanIds: [],
  serviceNames: [],
  serviceInstances: [],
  spanNames: [],
  duration: { min: null, max: null },
  isRoot: null,
  sort: "start_time",
  order: "desc",
  visibleColumns: undefined,
};

/** Default log grid filters. */
export const DEFAULT_LOG_FILTERS: GridLogFilters = {
  traceIds: [],
  spanIds: [],
  logIds: [],
  serviceNames: [],
  serviceInstances: [],
  severityRanges: [],
  bodyFilters: [],
  sort: "ts",
  order: "desc",
  visibleColumns: undefined,
};

function isValidStringArray(v: unknown): v is string[] {
  return Array.isArray(v) && v.every((x) => typeof x === "string");
}

function isValidDuration(v: unknown): v is GridDurationFilter {
  if (!v || typeof v !== "object") return false;
  const d = v as Record<string, unknown>;
  return (
    (d.min === null || (typeof d.min === "number" && !Number.isNaN(d.min))) &&
    (d.max === null || (typeof d.max === "number" && !Number.isNaN(d.max)))
  );
}

function isValidSpanCount(v: unknown): v is GridSpanCountFilter {
  if (!v || typeof v !== "object") return false;
  const d = v as Record<string, unknown>;
  return (
    (d.min === null || (typeof d.min === "number" && !Number.isNaN(d.min))) &&
    (d.max === null || (typeof d.max === "number" && !Number.isNaN(d.max)))
  );
}

function isValidTextFilter(v: unknown): v is GridTextFilter {
  if (!v || typeof v !== "object") return false;
  const t = v as Record<string, unknown>;
  return (
    typeof t.text === "string" &&
    ["contains", "prefix", "suffix", "equals"].includes(String(t.mode))
  );
}

function mergeTraceFilters(
  defaults: GridTraceFilters,
  stored: GridTraceFilters | null,
): GridTraceFilters {
  if (!stored) return defaults;

  const duration: GridDurationFilter = isValidDuration(stored.duration)
    ? stored.duration
    : defaults.duration;

  const spanCount: GridSpanCountFilter = isValidSpanCount(stored.spanCount)
    ? stored.spanCount
    : defaults.spanCount;

  return {
    traceIds: isValidStringArray(stored.traceIds) ? stored.traceIds : defaults.traceIds,
    serviceNames: isValidStringArray(stored.serviceNames)
      ? stored.serviceNames
      : defaults.serviceNames,
    serviceInstances: isValidStringArray(stored.serviceInstances)
      ? stored.serviceInstances
      : defaults.serviceInstances,
    duration,
    spanCount,
    sort:
      typeof stored.sort === "string" &&
      ["start_time", "end_time", "duration", "span_count"].includes(stored.sort)
        ? (stored.sort as GridTraceFilters["sort"])
        : defaults.sort,
    order: stored.order === "asc" || stored.order === "desc" ? stored.order : defaults.order,
    visibleColumns: isValidStringArray(stored.visibleColumns)
      ? stored.visibleColumns
      : defaults.visibleColumns,
  };
}

function mergeSpanFilters(
  defaults: GridSpanFilters,
  stored: GridSpanFilters | null,
): GridSpanFilters {
  if (!stored) return defaults;

  const duration: GridDurationFilter = isValidDuration(stored.duration)
    ? stored.duration
    : defaults.duration;

  const isRoot =
    stored.isRoot === true || stored.isRoot === false || stored.isRoot === null
      ? stored.isRoot
      : defaults.isRoot;

  return {
    traceIds: isValidStringArray(stored.traceIds) ? stored.traceIds : defaults.traceIds,
    spanIds: isValidStringArray(stored.spanIds) ? stored.spanIds : defaults.spanIds,
    serviceNames: isValidStringArray(stored.serviceNames)
      ? stored.serviceNames
      : defaults.serviceNames,
    serviceInstances: isValidStringArray(stored.serviceInstances)
      ? stored.serviceInstances
      : defaults.serviceInstances,
    spanNames: isValidStringArray(stored.spanNames) ? stored.spanNames : defaults.spanNames,
    duration,
    isRoot,
    sort:
      typeof stored.sort === "string" &&
      ["start_time", "end_time", "duration", "span_name"].includes(stored.sort)
        ? (stored.sort as GridSpanFilters["sort"])
        : defaults.sort,
    order: stored.order === "asc" || stored.order === "desc" ? stored.order : defaults.order,
    visibleColumns: isValidStringArray(stored.visibleColumns)
      ? stored.visibleColumns
      : defaults.visibleColumns,
  };
}

function mergeLogFilters(defaults: GridLogFilters, stored: GridLogFilters | null): GridLogFilters {
  if (!stored) return defaults;

  const bodyFilters = Array.isArray(stored.bodyFilters)
    ? (stored.bodyFilters as unknown[]).filter(isValidTextFilter)
    : defaults.bodyFilters;

  return {
    traceIds: isValidStringArray(stored.traceIds) ? stored.traceIds : defaults.traceIds,
    spanIds: isValidStringArray(stored.spanIds) ? stored.spanIds : defaults.spanIds,
    logIds: isValidStringArray(stored.logIds) ? stored.logIds : defaults.logIds,
    serviceNames: isValidStringArray(stored.serviceNames)
      ? stored.serviceNames
      : defaults.serviceNames,
    serviceInstances: isValidStringArray(stored.serviceInstances)
      ? stored.serviceInstances
      : defaults.serviceInstances,
    severityRanges: isValidStringArray(stored.severityRanges)
      ? stored.severityRanges
      : defaults.severityRanges,
    bodyFilters,
    sort:
      typeof stored.sort === "string" && ["ts", "severity_text"].includes(stored.sort)
        ? (stored.sort as GridLogFilters["sort"])
        : defaults.sort,
    order: stored.order === "asc" || stored.order === "desc" ? stored.order : defaults.order,
    visibleColumns: isValidStringArray(stored.visibleColumns)
      ? stored.visibleColumns
      : defaults.visibleColumns,
  };
}

export interface UseGridFiltersResult<T> {
  filters: T;
  setFilters: (value: T | ((prev: T) => T)) => void;
}

/**
 * Hook for grid column filters with localStorage persistence per service.
 * Loads from localStorage only on first mount; merges with defaultFilters (stored overrides defaults).
 * Persists to localStorage only when filters change via setFilters (user interaction).
 *
 * @param serviceSlug - Service slug for storage key scoping
 * @param gridType - traces | spans | logs
 * @param defaultFilters - Default values; stored values override these
 */
export function useGridFilters(
  serviceSlug: string | null,
  gridType: "traces",
  defaultFilters?: GridTraceFilters,
): UseGridFiltersResult<GridTraceFilters>;

export function useGridFilters(
  serviceSlug: string | null,
  gridType: "spans",
  defaultFilters?: GridSpanFilters,
): UseGridFiltersResult<GridSpanFilters>;

export function useGridFilters(
  serviceSlug: string | null,
  gridType: "logs",
  defaultFilters?: GridLogFilters,
): UseGridFiltersResult<GridLogFilters>;

export function useGridFilters<T extends GridTraceFilters | GridSpanFilters | GridLogFilters>(
  serviceSlug: string | null,
  gridType: GridType,
): UseGridFiltersResult<T> {
  const [filters, setFiltersState] = useState<T>(() => {
    if (gridType === "traces") {
      return DEFAULT_TRACE_FILTERS as T;
    }

    if (gridType === "spans") {
      return DEFAULT_SPAN_FILTERS as T;
    }

    if (gridType === "logs") {
      return DEFAULT_LOG_FILTERS as T;
    }

    throw new Error(`Invalid grid type: ${gridType}`);
  });

  // Reload from localStorage when serviceSlug or gridType changes (e.g. navigation to different service)
  useEffect(() => {
    if (gridType === "traces") {
      const stored = serviceSlug ? getGridFilters<GridTraceFilters>(serviceSlug, gridType) : null;
      setFiltersState(mergeTraceFilters(DEFAULT_TRACE_FILTERS, stored) as T);
    } else if (gridType === "spans") {
      const stored = serviceSlug ? getGridFilters<GridSpanFilters>(serviceSlug, gridType) : null;
      setFiltersState(mergeSpanFilters(DEFAULT_SPAN_FILTERS, stored) as T);
    } else {
      const stored = serviceSlug ? getGridFilters<GridLogFilters>(serviceSlug, gridType) : null;
      setFiltersState(mergeLogFilters(DEFAULT_LOG_FILTERS, stored) as T);
    }
  }, [serviceSlug, gridType]);

  const setFilters = useCallback(
    (value: T | ((prev: T) => T)) => {
      setFiltersState((prev) => {
        const next = typeof value === "function" ? (value as (p: T) => T)(prev) : value;
        if (serviceSlug) {
          setGridFilters(serviceSlug, gridType, next);
        }
        return next;
      });
    },
    [serviceSlug, gridType],
  );

  return { filters, setFilters };
}
